import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { brandingService } from '../services/companyService';
import { useAuth } from './AuthContext';

const BrandingContext = createContext(null);

/**
 * The token from a fill link: /fill/<token>, or /form/<formId> for the
 * staff-side preview. Used to resolve the owning company's branding before any
 * session exists.
 */
function readFillToken(pathname) {
  if (!pathname) return null;
  const m = pathname.match(/^\/(?:fill|form)\/([^/?#]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

const PLATFORM_DEFAULTS = {
  productName: 'inkedform',
  companyName: 'inkedform',
  // The mark, not the full logo: the sidebar renders its own wordmark beside
  // it, so a lockup image would repeat the name.
  logoUrl: '/images/mark.png',
  primaryColor: '#16c1d5',
  hasCustomLogo: false,
  hasCustomPrimaryColor: false,
  locale: 'en',
  isCompanyBranded: false,
  contactEmail: null,
  contactUrl: null,
};

/**
 * Resolves whose brand to render.
 *
 * Before login the server answers from the request host, which is what a
 * white-labelled company domain needs. Once signed in, the caller's own company
 * wins, so a platform owner browsing another company's data never mistakes that
 * company's brand for its own.
 */
export function BrandingProvider({ children }) {
  const { user } = useAuth();
  const [branding, setBranding] = useState(PLATFORM_DEFAULTS);
  const [loaded, setLoaded] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [companyOverride, setCompanyOverride] = useState(null);

  const location = useLocation();
  const companyId = user?.clinicId || null;
  const requestedCompanyId = location.pathname === '/admin/company'
    ? new URLSearchParams(location.search).get('company')
    : null;
  const resolvedCompanyId = companyId || requestedCompanyId || (location.pathname === '/admin/company' ? companyOverride : null);
  // A recipient has no session, so the fill link itself is the only thing that
  // says which company the form belongs to. Host resolution covers a company
  // on its own domain; the token covers everyone sharing the platform domain.
  const fillToken = readFillToken(location.pathname);

  useEffect(() => {
    let cancelled = false;

    if (!resolvedCompanyId) {
      // Signed out: resolve from the link, then the host, then the platform.
      setBranding(PLATFORM_DEFAULTS);
      setLoaded(false);
      brandingService
        .get(fillToken ? { token: fillToken } : {})
        .then((data) => {
          if (cancelled || !data) return;
          setBranding({ ...PLATFORM_DEFAULTS, ...data });
        })
        .catch(() => { /* keep defaults */ })
        .finally(() => { if (!cancelled) setLoaded(true); });
      return () => { cancelled = true; };
    }

    setLoaded(false);
    brandingService
      .get({ company: resolvedCompanyId })
      .then((data) => {
        if (cancelled || !data) return;
        setBranding({ ...PLATFORM_DEFAULTS, ...data });
      })
      .catch(() => { /* keep defaults */ })
      .finally(() => { if (!cancelled) setLoaded(true); });

    return () => { cancelled = true; };
  }, [resolvedCompanyId, fillToken, refreshKey]);

  useEffect(() => {
    const product = branding.productName || 'inkedform';
    const brandName = branding.isCompanyBranded
      ? (branding.companyName || product)
      : product;
    const path = location.pathname || '';
    document.title = (path === '/' || path === '/login')
      ? `Sign in · ${brandName}`
      : (path === '/privacy')
        ? `Privacy · ${brandName}`
        : (path === '/terms')
          ? `Terms · ${brandName}`
          : brandName;
  }, [branding.companyName, branding.isCompanyBranded, branding.productName, location.pathname]);

  useEffect(() => {
    const root = document.documentElement;
    // When a company has not set a custom primary, still publish the inkedform
    // platform default so login/buttons do not fall back to the old Form Portal navy.
    const raw = branding.hasCustomPrimaryColor
      ? branding.primaryColor
      : PLATFORM_DEFAULTS.primaryColor;
    const color = typeof raw === 'string' && /^#[\da-f]{6}$/i.test(raw)
      ? raw
      : PLATFORM_DEFAULTS.primaryColor;

    const channels = [1, 3, 5].map((offset) => parseInt(color.slice(offset, offset + 2), 16) / 255);
    const linear = channels.map((channel) => channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4);
    const luminance = 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
    const contrast = luminance > 0.20 ? '#111827' : '#ffffff';
    const overlay = luminance > 0.20 ? 'rgba(17, 24, 39, 0.10)' : 'rgba(255, 255, 255, 0.14)';
    root.style.setProperty('--company-primary-color', color);
    root.style.setProperty('--company-primary-contrast', contrast);
    root.style.setProperty('--company-primary-hover', `color-mix(in srgb, ${color} 88%, ${contrast})`);
    root.style.setProperty('--company-overlay', overlay);
    root.style.setProperty('--primary-color', color);
    root.style.setProperty('--reg-header-bg', color);
    root.style.setProperty('--reg-section-header-bg', color);
    root.style.setProperty('--reg-btn-next-bg', color);
    root.style.setProperty('--reg-btn-forward-bg', color);
    root.style.setProperty('--reg-btn-forward-hover', `color-mix(in srgb, ${color} 88%, ${contrast})`);
    root.style.setProperty('--reg-btn-submit-bg', color);
    root.style.setProperty('--reg-input-focus', color);
  }, [branding.primaryColor, branding.hasCustomPrimaryColor]);

  const refreshBranding = useCallback((targetCompanyId) => {
    if (targetCompanyId) setCompanyOverride(targetCompanyId);
    setRefreshKey((current) => current + 1);
  }, []);

  const value = useMemo(() => ({ branding, loaded, refreshBranding }), [branding, loaded, refreshBranding]);

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding() {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error('useBranding must be used within BrandingProvider');
  return ctx;
}
